"use client"
import React, { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import {
  ArrowLeft,
  ArrowRight,
  Braces,
  Check,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Copy,
  FileCode2,
  FolderOpen,
  Globe,
  Image as ImageIcon,
  Info,
  Languages,
  LayoutGrid,
  ListOrdered,
  Loader,
  Lock,
  Maximize2,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Rocket,
  Search,
  Table2,
  Trash2,
  Type,
  X,
} from "lucide-react"

import { Button } from "../ui/button"
import { Input } from "../ui/input"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu"
import SectionsManager from "../SectionsManager"
import { formatDate, updatedAtOf } from "../dashboard/ResourceBits"
import ImageCropper from "./ImageCropper"
import {
  getTemplateLanguageLabel,
  getOutputFormatModeLabel,
  OUTPUT_FORMAT_OPTIONS,
  TEMPLATE_LANGUAGE_OPTIONS,
} from "../../types/template.types"
import { nanoid } from "nanoid"
import { sampleFromJsonSchema } from "./lib/avatarMapper"
import { NEUTRAL_KIND_CLASS, resolveBlockIcon, resolveBlockKind } from "./lib/blockIcons"
import { readinessChecklist, readinessMessage } from "./lib/readiness"
import { STARTER_BLOCKS } from "./lib/starterBlocks"

const PANEL = "bg-white/5 backdrop-blur-sm rounded-xl border border-white/20"
const INSET = "rounded-xl border border-white/10 bg-black/20"

const TABS = [
  { id: "overview", label: "Overview", Icon: LayoutGrid },
  { id: "blocks", label: "Blocks", Icon: Braces },
  { id: "output", label: "Output", Icon: FileCode2 },
  { id: "examples", label: "Examples", Icon: ListOrdered },
];

/** Each output mode gets a card in the picker, like the mockup. */
const MODE_ICON = { normal: Type, json: Braces, markdown: FileCode2, html: Table2 };

/* ------------------------------------------------------------------ */
/*  Pieces                                                             */
/* ------------------------------------------------------------------ */

function ReadinessRing({ percent }) {
  const r = 42;
  const c = 2 * Math.PI * r;

  return (
    <svg viewBox="0 0 100 100" className="h-20 w-20 -rotate-90 sm:h-24 sm:w-24 lg:h-32 lg:w-32 xl:h-36 xl:w-36">
      <circle cx="50" cy="50" r={r} fill="none" strokeWidth="8" className="stroke-white/10" />
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - (c * percent) / 100}
        className="stroke-[#a855f7] transition-all duration-700"
      />
    </svg>
  );
}

/**
 * A labelled fact in the hero's meta row.
 *
 * The divider only appears at lg, where the five items sit on one line. Once
 * the grid wraps, a left border lands mid-row and reads as a stray line.
 */
function MetaItem({ Icon, label, value }) {
  return (
    <div className="min-w-0 lg:border-l lg:border-white/10 lg:pl-4 lg:first:border-l-0 lg:first:pl-0">
      <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </p>
      {/* Wraps rather than truncates: "Spanish (Espa…" is worse than two lines. */}
      <p className="mt-1 break-words text-sm text-white lg:truncate">{value}</p>
    </div>
  );
}

function SummaryTile({ Icon, label, value, sub, tone }) {
  const tones = {
    purple: "bg-purple-500/15 text-purple-300",
    blue: "bg-blue-500/15 text-blue-300",
    green: "bg-green-500/15 text-green-300",
    orange: "bg-orange-500/15 text-orange-300",
    pink: "bg-pink-500/15 text-pink-300",
  };

  return (
    /* Two shapes, because two columns of tiles leave the text about sixty pixels
       and it starts hyphenating itself — "Instruc / tion blocks", "Visibilit / y".
       Below sm this is one column of rows: icon, label, value on the right. From
       sm it is the tile it always was. */
    <div className={`${INSET} p-3 sm:p-4`}>
      <div className="flex items-center gap-3">
        <span
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9 ${tones[tone]}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <p className="min-w-0 flex-1 text-sm text-gray-400 sm:text-xs">{label}</p>
        {/* Bold carries the emphasis here; size on top of it just shouts. */}
        <p className="flex-shrink-0 text-sm font-semibold text-white sm:hidden">{value}</p>
      </div>

      <p className="mt-3 hidden truncate text-xl font-semibold text-white sm:block">{value}</p>
      {/* The sub explains the number — worth a line, but only where there is room. */}
      {sub && <p className="mt-0.5 hidden text-xs text-gray-500 sm:block">{sub}</p>}
    </div>
  );
}

/** Roughly how long a block is — a real fact, unlike a per-block timestamp. */
const wordCount = (entry) =>
  (entry.content || entry.markdown || "").trim().split(/\s+/).filter(Boolean).length;

function BlockRow({ entry, index, total, onOpen, onEdit, onCopy, isOwner }) {
  const Icon = resolveBlockIcon(entry);
  const kind = resolveBlockKind(entry);
  const words = wordCount(entry);

  return (
    <div
      onClick={() => onOpen(entry, index)}
      className="group flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3 transition-all hover:border-purple-400/50 hover:bg-black/30 sm:gap-4 sm:p-4"
    >
      {/* The icon carries the block's kind as colour, so the list is scannable
          without reading a single label. On a phone it does most of the work,
          which is why it stays while the badge and the excerpt give way. */}
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border sm:h-11 sm:w-11 sm:rounded-xl ${
          kind ? kind.className : NEUTRAL_KIND_CLASS
        }`}
      >
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* Wraps at spaces, not mid-word: "Identid…" and "Identida/d" are both
              worse than two clean lines. */}
          <p className="min-w-0 text-sm font-medium text-white sm:truncate sm:text-base">
            {entry.title || entry.name || `Block ${index + 1}`}
          </p>
          {kind && (
            <span
              className={`hidden rounded-full border px-2 py-0.5 text-[10px] font-medium sm:inline ${kind.className}`}
            >
              {kind.label}
            </span>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-xs text-gray-400 sm:line-clamp-1 sm:text-sm">
          {entry.content || entry.markdown || "No content"}
        </p>
      </div>

      <div className="hidden w-28 flex-shrink-0 lg:block">
        <p className="text-[11px] text-gray-500">Position</p>
        <p className="mt-0.5 text-sm text-gray-300">
          {index + 1} of {total}
        </p>
      </div>

      <div className="hidden w-24 flex-shrink-0 xl:block">
        <p className="text-[11px] text-gray-500">Length</p>
        <p className="mt-0.5 text-sm text-gray-300">{words} words</p>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="flex-shrink-0">
            <MoreHorizontal className="h-4 w-4 text-gray-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border border-white/40 bg-gray-900">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onOpen(entry, index);
            }}
            className="cursor-pointer text-white hover:bg-white/10"
          >
            <Search className="mr-2 h-4 w-4" />
            View full content
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onCopy(entry.content || entry.markdown || "");
            }}
            className="cursor-pointer text-white hover:bg-white/10"
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy content
          </DropdownMenuItem>
          {isOwner && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="cursor-pointer text-white hover:bg-white/10"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ChevronRight className="hidden h-5 w-5 flex-shrink-0 text-gray-600 transition-colors group-hover:text-purple-300 sm:block" />
    </div>
  );
}

function ExampleRow({ entry, index, onOpen }) {
  const Icon = resolveBlockIcon(entry, { isExample: true });

  return (
    <button
      type="button"
      onClick={() => onOpen(entry, index)}
      className="group flex w-full items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3 text-left transition-all hover:border-orange-400/50 hover:bg-black/30 sm:gap-4 sm:p-4"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-orange-500/15 text-orange-300 sm:h-11 sm:w-11 sm:rounded-xl">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium text-white sm:truncate sm:text-base">
          {entry.title || entry.name || `Example ${index + 1}`}
        </p>
        <p className="mt-1 line-clamp-2 text-xs text-gray-400 sm:text-sm">
          {entry.content || entry.markdown || "No content"}
        </p>
      </div>

      <div className="hidden w-24 flex-shrink-0 lg:block">
        <p className="text-[11px] text-gray-500">Length</p>
        <p className="mt-0.5 text-sm text-gray-300">{wordCount(entry)} words</p>
      </div>

      <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-600 transition-colors group-hover:text-orange-300" />
    </button>
  );
}

/**
 * The avatar's image, at the size it is actually stored.
 *
 * The hero shows it at 224px; the file is a 512 square. Portalled to the body
 * because the panel it sits in has a backdrop-filter, which would otherwise
 * become the containing block and trap this inside the hero.
 */
function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const body = (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm sm:p-8"
    >
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-xl border border-white/20 object-contain shadow-2xl"
      />
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-gray-300 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  return typeof document === "undefined" ? body : createPortal(body, document.body);
}

function EmptyTab({ Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/15 bg-black/10 py-16 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/5">
        <Icon className="h-6 w-6 text-gray-500" />
      </span>
      <p className="text-white">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{sub}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AvatarDetail({
  template,
  form,
  isOwner,
  isEditing,
  submitting,
  onBack,
  onEdit,
  onCancelEdit,
  onSave,
  onDuplicate,
  onDelete,
  onCopyId,
  onCopyText,
  onOpenEntry,
}) {
  const {
    name,
    description,
    tags,
    avatarUrl,
    avatarBase64,
    language,
    outputFormat,
    jsonSchema,
    jsonSchemaFields,
    jsonSchemaBuilderMode,
    sections,
    examples,
    isPublic,
    projects,
    projectsError,
    selectedProjects,
    validationErrors,
  } = form;

  const [tab, setTab] = useState("overview");
  const [pendingImage, setPendingImage] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [exampleQuery, setExampleQuery] = useState("");
  const [showChecklist, setShowChecklist] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showRawSchema, setShowRawSchema] = useState(false);
  const isLongDescription = (description || "").length > 140;

  /** The sample response shown in the Output tab, built from the schema. */
  const previewJson = useMemo(() => {
    if (outputFormat !== "json" || !jsonSchema) return "";
    try {
      const sample = sampleFromJsonSchema(JSON.parse(jsonSchema));
      return sample ? JSON.stringify(sample, null, 2) : "";
    } catch {
      // A half-typed schema in raw mode is not a crash — just nothing to show.
      return "";
    }
  }, [outputFormat, jsonSchema]);

  // A failed save must land you where the problem is: flagging "add a block"
  // while you're looking at the Overview tab would be invisible.
  useEffect(() => {
    if (validationErrors.sections) setTab("blocks");
    else if (validationErrors.name || validationErrors.projects) setTab("overview");
  }, [validationErrors.sections, validationErrors.name, validationErrors.projects]);

  const readiness = useMemo(
    () =>
      readinessChecklist({
        avatarUrl,
        avatarBase64,
        description,
        sections,
        examples,
        tags,
        outputFormat,
        jsonSchemaFields,
      }),
    [avatarUrl, avatarBase64, description, sections, examples, tags, outputFormat, jsonSchemaFields]
  );

  const projectNames = useMemo(
    () =>
      projects
        .filter((p) => selectedProjects.includes(p.project_id))
        .map((p) => p.name)
        .join(", "),
    [projects, selectedProjects]
  );

  const filteredExamples = useMemo(() => {
    const q = exampleQuery.trim().toLowerCase();
    if (!q) return examples;
    return examples.filter((e) =>
      `${e.title || e.name || ""} ${e.content || e.markdown || ""}`.toLowerCase().includes(q)
    );
  }, [examples, exampleQuery]);

  const image = avatarBase64 || avatarUrl;
  const isReady = readiness.percent >= 80;
  const createdAt = template?.createdAt || template?.created_at;

  return (
    <div className="h-full overflow-y-auto pb-8">
      {/* ---------------- Header ---------------- */}
      {/* One row, always. Letting it wrap left the actions stranded on a line
          of their own with an empty band above them. */}
      <div className="sticky top-0 z-30 -mx-2 mb-4 bg-[#0b0b12]/80 px-2 py-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              aria-label="Back to avatars"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {/* The back arrow already says "up". On a phone the crumb is just
                noise competing with the name for the little width there is. */}
            <button
              type="button"
              onClick={onBack}
              className="hidden flex-shrink-0 text-sm text-gray-400 transition-colors hover:text-white sm:block"
            >
              Avatars
            </button>
            <span className="hidden flex-shrink-0 text-gray-600 sm:inline">/</span>
            <span className="truncate text-sm font-medium text-purple-300">{name}</span>
          </div>

          {/* Editing happens inside this view, so the header swaps its actions
              rather than sending you to another screen. */}
          {isEditing ? (
            <div className="flex flex-shrink-0 items-center gap-2">
              <span className="mr-1 hidden text-xs text-amber-300 sm:inline">Editing</span>
              <Button
                onClick={onCancelEdit}
                disabled={submitting}
                variant="ghost"
                className="border border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={submitting}
                className="bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white hover:brightness-110"
              >
                {submitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin sm:mr-2" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Save changes</span>
                    <span className="sm:hidden">Save</span>
                  </>
                )}
              </Button>
            </div>
          ) : (
            /* Three buttons plus a menu do not fit on a phone. Below sm only
               the primary action stays; the rest fold into the menu, which is
               the only place Delete and Copy ID ever lived. */
            <div className="flex flex-shrink-0 items-center gap-2">
              {isOwner && (
                <Button
                  onClick={onEdit}
                  variant="ghost"
                  className="border border-white/15 bg-white/5 text-white hover:bg-white/10"
                >
                  <Pencil className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Edit Avatar</span>
                </Button>
              )}
              <Button
                onClick={onDuplicate}
                variant="ghost"
                className="hidden border border-white/15 bg-white/5 text-white hover:bg-white/10 md:inline-flex"
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </Button>
              {isOwner && (
                <Button
                  onClick={onEdit}
                  className="hidden bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white hover:brightness-110 md:inline-flex"
                  title={
                    isPublic
                      ? "Already public — edit to change visibility"
                      : "Edit to make this avatar public"
                  }
                >
                  <Rocket className="mr-2 h-4 w-4" />
                  {isPublic ? "Published" : "Publish"}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border border-white/40 bg-gray-900">
                  <DropdownMenuItem
                    onClick={onDuplicate}
                    className="cursor-pointer text-white hover:bg-white/10 md:hidden"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  {isOwner && (
                    <DropdownMenuItem
                      onClick={onEdit}
                      className="cursor-pointer text-white hover:bg-white/10 md:hidden"
                    >
                      <Rocket className="mr-2 h-4 w-4" />
                      {isPublic ? "Published" : "Publish"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onCopyId(template?.id)}
                    className="cursor-pointer text-white hover:bg-white/10"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy ID
                  </DropdownMenuItem>
                  {isOwner && !template?.isDefault && (
                    <DropdownMenuItem
                      onClick={() => onDelete(template?.id)}
                      className="text-red-400 hover:bg-red-900/60 hover:text-red-300"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[1800px] px-2">
        {/* ---------------- Tab bar ---------------- */}
        <div className={`${PANEL} mb-4 px-2`}>
          {/* The tabs must keep their natural width: without shrink-0, flex
              squeezes them, the labels spill out of their own boxes and the
              row reports as fitting — so it never scrolls. */}
          <div className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map(({ id, label, Icon }) => {
              const active = tab === id;
              const count =
                id === "blocks" ? sections.length : id === "examples" ? examples.length : null;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  aria-current={active ? "page" : undefined}
                  className={`relative flex flex-shrink-0 items-center gap-2 whitespace-nowrap px-3 py-3 text-xs font-medium transition-colors sm:px-5 sm:py-4 sm:text-sm ${
                    active ? "text-purple-300" : "text-gray-400 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {count > 0 && (
                    <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-gray-300">
                      {count}
                    </span>
                  )}
                  {active && (
                    <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-purple-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------------- Overview ---------------- */}
        {tab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {/* Hero */}
              <div className={`${PANEL} p-4 sm:p-6 xl:col-span-2`}>
                <div className="flex flex-col gap-6 sm:flex-row">
                  <div className="relative mx-auto h-36 w-36 flex-shrink-0 sm:mx-0 sm:h-56 sm:w-56">
                    <div className="h-full w-full overflow-hidden rounded-2xl border border-white/20 bg-black/40">
                      {image ? (
                        // The hero is 224px at most, and the stored image is 512
                        // square — clicking it shows what is actually there.
                        <button
                          type="button"
                          onClick={() => setZoomed(true)}
                          aria-label="View image full size"
                          className="group relative block h-full w-full"
                        >
                          <img src={image} alt={name} className="h-full w-full object-cover" />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                            <Maximize2 className="h-6 w-6 text-white" />
                          </span>
                        </button>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <ImageIcon className="h-10 w-10 text-gray-600" />
                        </div>
                      )}
                    </div>
                    {isOwner && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            isEditing
                              ? document.getElementById("avatar-detail-upload")?.click()
                              : onEdit()
                          }
                          aria-label="Change image"
                          className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-primary text-white shadow-lg transition-colors hover:bg-purple-500"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <input
                          id="avatar-detail-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (!file.type.startsWith("image/")) {
                              form.onImageError("Please select an image file");
                              e.target.value = "";
                              return;
                            }
                            if (file.size > 10 * 1024 * 1024) {
                              form.onImageError("File size must be less than 10MB");
                              e.target.value = "";
                              return;
                            }
                            // Framed in the cropper before it is stored — see
                            // ImageCropper. Nothing is resized behind the user's back.
                            setPendingImage(file);
                            e.target.value = "";
                          }}
                        />
                      </>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      {isEditing ? (
                        <div className="w-full">
                          <Input
                            value={name}
                            onChange={(e) => {
                              form.setName(e.target.value);
                              if (e.target.value.trim()) form.clearValidationError("name");
                            }}
                            placeholder="Avatar name"
                            className={`w-full bg-black/60 text-lg font-semibold text-white ${
                              validationErrors.name
                                ? "border-red-400 focus:border-red-400"
                                : "border-white/20 focus:border-purple-400"
                            }`}
                          />
                          {validationErrors.name && (
                            <p className="mt-1 text-xs text-red-400">
                              Please enter a name for your avatar
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <h1 className="text-xl font-semibold text-white sm:text-2xl">{name}</h1>
                          <span
                            className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                              isReady
                                ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                                : "border-amber-400/30 bg-amber-500/10 text-amber-300"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                isReady ? "bg-emerald-400" : "bg-amber-400"
                              }`}
                            />
                            {isReady ? "Ready" : "In progress"}
                          </span>
                        </>
                      )}
                    </div>

                    {isEditing ? (
                      <textarea
                        rows={3}
                        value={description}
                        onChange={(e) => form.setDescription(e.target.value)}
                        placeholder="Describe this avatar's purpose and capabilities"
                        className="mt-3 w-full resize-none rounded-lg border border-white/20 bg-black/60 px-4 py-3 text-sm text-white placeholder-gray-500"
                      />
                    ) : (
                      /* A long description would otherwise push everything else
                         below the fold on a phone. */
                      <div className="mt-2">
                        <p
                          className={`whitespace-pre-wrap text-sm leading-relaxed text-gray-300 ${
                            showFullDescription ? "" : "line-clamp-3 sm:line-clamp-none"
                          }`}
                        >
                          {description || "No description provided"}
                        </p>
                        {isLongDescription && (
                          <button
                            type="button"
                            onClick={() => setShowFullDescription((v) => !v)}
                            className="mt-1 text-xs text-purple-400 transition-colors hover:text-purple-300 sm:hidden"
                          >
                            {showFullDescription ? "Show less" : "Show more"}
                          </button>
                        )}
                      </div>
                    )}

                    {isEditing ? (
                      /* Language and visibility are the only meta you can set;
                         the rest are facts about the avatar, not choices. */
                      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-xs text-gray-400">Language</label>
                          <Select value={language} onValueChange={form.setLanguage}>
                            <SelectTrigger className="w-full border border-white/20 bg-black/60 text-white">
                              <SelectValue placeholder="Select language" />
                            </SelectTrigger>
                            <SelectContent className="border border-white/40 bg-gray-900">
                              {TEMPLATE_LANGUAGE_OPTIONS.map((lang) => (
                                <SelectItem
                                  key={lang.value}
                                  value={lang.value}
                                  className="cursor-pointer py-2 text-white hover:bg-white/10 focus:bg-white/10"
                                >
                                  {lang.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="mb-2 block text-xs text-gray-400">Visibility</label>
                          <Select
                            value={isPublic ? "public" : "private"}
                            onValueChange={(v) => form.setIsPublic(v === "public")}
                          >
                            <SelectTrigger className="w-full border border-white/20 bg-black/60 text-white">
                              <SelectValue placeholder="Select visibility" />
                            </SelectTrigger>
                            <SelectContent className="border border-white/40 bg-gray-900">
                              <SelectItem
                                value="private"
                                className="cursor-pointer py-2 text-white hover:bg-white/10 focus:bg-white/10"
                              >
                                Private (only my projects)
                              </SelectItem>
                              <SelectItem
                                value="public"
                                className="cursor-pointer py-2 text-white hover:bg-white/10 focus:bg-white/10"
                              >
                                Public (anyone can use)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* A private avatar has to live in at least one project. */}
                        {!isPublic && (
                          <div className="sm:col-span-2">
                            <label className="mb-2 block text-xs text-gray-400">
                              Shared with projects *
                            </label>
                            {projectsError ? (
                              <p className="text-sm text-red-400">{projectsError}</p>
                            ) : (
                              <>
                                <div
                                  className={`grid max-h-36 grid-cols-1 gap-2 overflow-y-auto rounded-xl border p-3 lg:grid-cols-2 ${
                                    validationErrors.projects
                                      ? "border-red-400 bg-red-900/20"
                                      : "border-white/20 bg-white/5"
                                  }`}
                                >
                                  {projects.length === 0 ? (
                                    <p className="text-sm text-gray-400">No projects found</p>
                                  ) : (
                                    projects.map((project) => (
                                      <label
                                        key={project.project_id}
                                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm text-white/90 transition hover:bg-blue-900/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedProjects.includes(project.project_id)}
                                          onChange={(e) => {
                                            const next = e.target.checked
                                              ? [...selectedProjects, project.project_id]
                                              : selectedProjects.filter(
                                                  (id) => id !== project.project_id
                                                );
                                            form.setSelectedProjects(next);
                                            if (next.length > 0) form.clearValidationError("projects");
                                          }}
                                          className="h-4 w-4 accent-blue-500"
                                        />
                                        <span className="truncate">{project.name}</span>
                                      </label>
                                    ))
                                  )}
                                </div>
                                {validationErrors.projects && (
                                  <p className="mt-1 text-xs text-red-400">
                                    Pick at least one project to share this private avatar with
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        <MetaItem
                          Icon={FolderOpen}
                          label="Project"
                          value={isPublic ? "Public" : projectNames || "None"}
                        />
                        <MetaItem
                          Icon={Languages}
                          label="Language"
                          value={getTemplateLanguageLabel(language)}
                        />
                        <MetaItem
                          Icon={FileCode2}
                          label="Output format"
                          value={getOutputFormatModeLabel(outputFormat)}
                        />
                        <MetaItem
                          Icon={Clock}
                          label="Created"
                          value={createdAt ? formatDate(createdAt) : "—"}
                        />
                        <MetaItem
                          Icon={Clock}
                          label="Last updated"
                          value={formatDate(updatedAtOf(template || {}))}
                        />
                      </div>
                    )}

                    {/* Tags */}
                    {isEditing ? (
                      <div className="mt-5">
                        <label className="mb-2 block text-xs text-gray-400">Tags</label>
                        <div className="flex min-h-[52px] flex-wrap items-center gap-2 rounded-lg border border-white/20 bg-black/40 p-3">
                          {tags.map((tag, i) => (
                            <span
                              key={i}
                              className="group inline-flex items-center gap-1.5 rounded-full border border-purple-500/40 bg-purple-600/20 px-3 py-1.5 text-sm text-purple-200"
                            >
                              # {tag}
                              <button
                                type="button"
                                onClick={() => form.handleRemoveTag(tag)}
                                aria-label={`Remove ${tag}`}
                                className="rounded-full p-0.5 transition-colors hover:bg-red-500/20"
                              >
                                <X className="h-3 w-3 text-purple-300 group-hover:text-red-400" />
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={form.tagInput}
                            onChange={(e) => form.setTagInput(e.target.value)}
                            onKeyDown={form.handleTagInputKeyDown}
                            placeholder={tags.length === 0 ? "Type and press Enter..." : "Add more..."}
                            className="min-w-[120px] flex-1 border-none bg-transparent text-sm text-white placeholder-gray-500 outline-none"
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Press Enter or comma to add · lowercase-with-dashes
                        </p>
                      </div>
                    ) : (
                      <div className="mt-5 flex flex-wrap items-center gap-2">
                        {tags.map((tag, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-purple-500/30 bg-purple-600/15 px-2.5 py-1 text-xs text-purple-200"
                          >
                            # {tag}
                          </span>
                        ))}
                        {isOwner && (
                          <button
                            type="button"
                            onClick={onEdit}
                            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-gray-300 transition-colors hover:border-purple-400/50 hover:text-purple-200"
                          >
                            <Plus className="h-3 w-3" />
                            Add tag
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Readiness */}
              <div className={`${PANEL} p-4 sm:p-6`}>
                <h3 className="text-sm font-semibold text-white lg:text-base">Readiness Score</h3>
                {/* The ring stays beside its text at every width — stacking them
                    made this card taller than the hero it sits next to. It is
                    kept small on a phone, where the row is tight, and given the
                    room it has on a desktop, where the panel is mostly air. */}
                <div className="mt-4 flex items-center gap-4 lg:mt-6 lg:gap-6">
                  <div className="relative flex-shrink-0">
                    <ReadinessRing percent={readiness.percent} />
                    <span className="absolute inset-0 flex items-center justify-center text-base font-semibold text-white sm:text-lg lg:text-2xl xl:text-3xl">
                      {readiness.percent}%
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-300 lg:text-base">{readinessMessage(readiness.percent)}</p>
                    <button
                      type="button"
                      onClick={() => setShowChecklist((v) => !v)}
                      className="mt-2 inline-flex items-center gap-1 whitespace-nowrap text-sm text-purple-400 transition-colors hover:text-purple-300"
                    >
                      {showChecklist ? "Hide checklist" : "See checklist"}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {showChecklist && (
                  <ul className="mt-4 space-y-2 border-t border-white/10 pt-4">
                    {readiness.items.map((item) => (
                      <li key={item.id} className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full ${
                            item.done
                              ? "bg-emerald-500/20 text-emerald-300"
                              : "bg-white/10 text-gray-600"
                          }`}
                        >
                          {item.done && <Check className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0">
                          <span
                            className={`text-sm ${item.done ? "text-gray-300" : "text-gray-400"}`}
                          >
                            {item.label}
                          </span>
                          {!item.done && (
                            <span className="block text-xs text-gray-600">{item.hint}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Configuration summary */}
            <div className={`${PANEL} p-4 sm:p-6`}>
              <h3 className="text-base font-medium text-white sm:text-lg">Configuration summary</h3>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">A quick read of how this avatar is set up.</p>

              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
                <SummaryTile
                  Icon={Braces}
                  label="Instruction blocks"
                  value={sections.length}
                  sub="Configured"
                  tone="purple"
                />
                <SummaryTile
                  Icon={MessageSquare}
                  label="Examples"
                  value={examples.length}
                  sub="Conversation cases"
                  tone="orange"
                />
                <SummaryTile
                  Icon={FileCode2}
                  label="Output format"
                  value={getOutputFormatModeLabel(outputFormat)}
                  sub={outputFormat === "json" ? `${jsonSchemaFields.length} fields` : "Plain text"}
                  tone="green"
                />
                <SummaryTile
                  Icon={Languages}
                  label="Language"
                  value={(language || "es").toUpperCase()}
                  sub={getTemplateLanguageLabel(language)}
                  tone="blue"
                />
                <SummaryTile
                  Icon={isPublic ? Globe : Lock}
                  label="Visibility"
                  value={isPublic ? "Public" : "Private"}
                  sub={isPublic ? "Anyone can use it" : "Shared by project"}
                  tone="pink"
                />
              </div>
            </div>

            {/* Configuration map */}
            {sections.length > 0 && (
              <div className={`${PANEL} p-4 sm:p-6`}>
                <h3 className="text-base font-medium text-white sm:text-lg">Configuration map</h3>
                <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                  The blocks that make up this avatar, in the order the model reads them.
                </p>

                <div className="mt-5 flex items-start gap-0 overflow-x-auto pb-2">
                  {sections.map((section, idx) => {
                    const Icon = resolveBlockIcon(section);
                    const kind = resolveBlockKind(section);

                    return (
                      <React.Fragment key={section.id || idx}>
                        {idx > 0 && (
                          <span
                            aria-hidden
                            className="mt-7 h-px w-8 flex-shrink-0 border-t border-dashed border-white/20"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setTab("blocks");
                            onOpenEntry(section, idx, "section");
                          }}
                          className="flex w-24 flex-shrink-0 flex-col items-center gap-2 rounded-xl p-2 text-center transition-colors hover:bg-white/5"
                        >
                          <span
                            className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${
                              kind ? kind.className : NEUTRAL_KIND_CLASS
                            }`}
                          >
                            <Icon className="h-6 w-6" />
                          </span>
                          <span className="line-clamp-2 text-xs text-gray-300">
                            {section.title || section.name || `Block ${idx + 1}`}
                          </span>
                          {kind && (
                            <span className="text-[10px] text-gray-500">{kind.label}</span>
                          )}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div className={`${PANEL} p-4 sm:p-6`}>
              <h3 className="text-base font-medium text-white sm:text-lg">Quick actions</h3>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  {
                    key: "edit-blocks",
                    Icon: Braces,
                    title: "Edit blocks",
                    sub: "Change instructions and behaviour",
                    run: onEdit,
                    ownerOnly: true,
                  },
                  {
                    key: "examples",
                    Icon: MessageSquare,
                    title: "See examples",
                    sub: "Review the conversation cases",
                    run: () => setTab("examples"),
                  },
                  {
                    key: "output",
                    Icon: FileCode2,
                    title: "Check output",
                    sub: "Response format and structure",
                    run: () => setTab("output"),
                  },
                  {
                    key: "duplicate",
                    Icon: Copy,
                    title: "Duplicate avatar",
                    sub: "Start a new one from this",
                    run: onDuplicate,
                  },
                ]
                  .filter((a) => !a.ownerOnly || isOwner)
                  .map(({ key, Icon, title, sub, run }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={run}
                      className={`flex items-center gap-3 p-4 text-left transition-all hover:border-purple-400/50 hover:bg-black/30 ${INSET}`}
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-500/15 text-purple-300">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-white">{title}</span>
                        <span className="block text-xs text-gray-500">{sub}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-600" />
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ---------------- Blocks ---------------- */}
        {tab === "blocks" && (
          <div className={`${PANEL} p-4 sm:p-6`}>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 text-base font-medium text-white sm:text-lg">
                  Instruction blocks
                  <span title="Blocks are concatenated in order to build this avatar's prompt.">
                    <Info className="h-4 w-4 text-gray-500" />
                  </span>
                </h3>
                <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                  These define this avatar's behaviour and rules.
                </p>
              </div>
              {isOwner && !isEditing && (
                <Button onClick={onEdit} className="bg-primary text-white hover:bg-violet-700">
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit blocks
                </Button>
              )}
            </div>

            {isEditing ? (
              <div
                className={`flex min-h-[420px] flex-col rounded-lg border p-2 sm:p-4 ${
                  validationErrors.sections
                    ? "border-red-400 bg-red-900/20"
                    : "border-white/10 bg-black/20"
                }`}
              >
                <SectionsManager
                  sections={sections}
                  setSections={(next) => {
                    form.setSections(next);
                    if (next.length > 0) form.clearValidationError("sections");
                  }}
                  placeholder="Define the sections of your avatar..."
                  helperTip="Think of blocks as instructions for personality and behavior. Start with identity, tone, boundaries, and response structure."
                  starterSections={STARTER_BLOCKS}
                />
                {validationErrors.sections && (
                  <p className="mt-2 text-xs text-red-400">
                    Add at least one block to define your avatar's behaviour
                  </p>
                )}
              </div>
            ) : sections.length > 0 ? (
              <div className="space-y-2">
                {sections.map((section, idx) => (
                  <BlockRow
                    key={section.id || idx}
                    entry={section}
                    index={idx}
                    total={sections.length}
                    isOwner={isOwner}
                    onOpen={(e, i) => onOpenEntry(e, i, "section")}
                    onEdit={onEdit}
                    onCopy={onCopyText}
                  />
                ))}

                {isOwner && (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-purple-500/40 bg-purple-600/5 py-6 transition-colors hover:border-purple-400 hover:bg-purple-600/10"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-purple-300">
                      <Plus className="h-4 w-4" />
                      Add new block
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      Create a custom instruction block
                    </span>
                  </button>
                )}

              </div>
            ) : (
              <EmptyTab
                Icon={Braces}
                title="No blocks yet"
                sub="Blocks are the instructions that shape how this avatar answers."
              />
            )}
          </div>
        )}

        {/* ---------------- Output ---------------- */}
        {tab === "output" && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className={`${PANEL} p-4 sm:p-6 xl:col-span-2`}>
              <h3 className="text-base font-medium text-white sm:text-lg">Output configuration</h3>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                How this avatar formats and structures its responses.
              </p>

              {/* Output mode. Four options are four options — stacking them into
                  four full-width cards ate a whole screen on a phone. They stay
                  on one row; the blurb is what gives way. */}
              <p className="mt-6 text-xs uppercase tracking-wide text-gray-500">Output mode</p>
              <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3">
                {OUTPUT_FORMAT_OPTIONS.map((opt) => {
                  const Icon = MODE_ICON[opt.value] || Type;
                  const selected = outputFormat === opt.value;

                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={!isEditing}
                      onClick={() => form.setOutputFormat(opt.value)}
                      className={`relative flex flex-col items-center rounded-xl border p-2.5 text-center transition-all sm:items-start sm:p-4 sm:text-left ${
                        selected
                          ? "border-purple-500/60 bg-purple-600/10"
                          : isEditing
                          ? "border-white/10 bg-black/20 hover:border-purple-400/50 hover:bg-black/30"
                          : "border-white/10 bg-black/20 opacity-60"
                      }`}
                    >
                      {selected && (
                        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-purple-500 text-white sm:right-2.5 sm:top-2.5 sm:h-5 sm:w-5">
                          <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                        </span>
                      )}
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-lg sm:h-9 sm:w-9 ${
                          selected
                            ? "bg-purple-500/25 text-purple-200"
                            : "bg-white/5 text-gray-400"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <p className="mt-2 break-words text-[11px] font-medium leading-tight text-white sm:mt-3 sm:text-sm">
                        {opt.label}
                      </p>
                      <p className="mt-0.5 hidden text-xs text-gray-500 sm:block">
                        {opt.description}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Response structure */}
              {outputFormat === "json" && (
                <>
                  <div className="mt-8 flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Response structure
                    </p>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => form.setJsonSchemaBuilderMode(!jsonSchemaBuilderMode)}
                        className="text-xs text-purple-400 transition-colors hover:text-purple-300"
                      >
                        {jsonSchemaBuilderMode ? "</> Raw JSON" : "Builder"}
                      </button>
                    )}
                  </div>

                  {isEditing && jsonSchemaBuilderMode ? (
                    <div className="mt-3 space-y-3">
                      {jsonSchemaFields.map((field, index) => (
                        <div key={field.id} className={`${INSET} space-y-2 p-3`}>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs text-gray-400">Field name *</label>
                              <input
                                type="text"
                                value={field.name}
                                onChange={(e) => {
                                  const next = [...jsonSchemaFields];
                                  next[index] = { ...next[index], name: e.target.value };
                                  form.setJsonSchemaFields(next);
                                }}
                                placeholder="e.g. intent"
                                className="w-full rounded border border-white/20 bg-black/60 px-2 py-1 text-sm text-white"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs text-gray-400">Type</label>
                              <select
                                value={field.type}
                                onChange={(e) => {
                                  const next = [...jsonSchemaFields];
                                  next[index] = { ...next[index], type: e.target.value };
                                  form.setJsonSchemaFields(next);
                                }}
                                className="w-full rounded border border-white/20 bg-black/60 px-2 py-1 text-sm text-white"
                              >
                                {["string", "number", "integer", "boolean", "array", "object"].map(
                                  (t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  )
                                )}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-gray-400">Description</label>
                            <input
                              type="text"
                              value={field.description}
                              onChange={(e) => {
                                const next = [...jsonSchemaFields];
                                next[index] = { ...next[index], description: e.target.value };
                                form.setJsonSchemaFields(next);
                              }}
                              placeholder="What this field holds"
                              className="w-full rounded border border-white/20 bg-black/60 px-2 py-1 text-sm text-white"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-300">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => {
                                  const next = [...jsonSchemaFields];
                                  next[index] = { ...next[index], required: e.target.checked };
                                  form.setJsonSchemaFields(next);
                                }}
                                className="rounded border-white/20"
                              />
                              Required field
                            </label>
                            <button
                              type="button"
                              onClick={() =>
                                form.setJsonSchemaFields(
                                  jsonSchemaFields.filter((_, i) => i !== index)
                                )
                              }
                              className="text-xs text-red-400 hover:text-red-300"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() =>
                          form.setJsonSchemaFields([
                            ...jsonSchemaFields,
                            {
                              id: nanoid(),
                              name: "",
                              type: "string",
                              description: "",
                              required: false,
                            },
                          ])
                        }
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-500/40 bg-purple-600/20 py-2 text-sm text-purple-300 transition-colors hover:bg-purple-600/30"
                      >
                        <Plus className="h-4 w-4" />
                        Add field
                      </button>
                    </div>
                  ) : isEditing ? (
                    <textarea
                      value={jsonSchema}
                      onChange={(e) => form.setJsonSchema(e.target.value)}
                      placeholder='{ "type": "json_schema", "json_schema": { ... } }'
                      className="mt-3 h-80 w-full resize-none rounded-lg border border-white/20 bg-black/60 px-4 py-3 font-mono text-xs text-white placeholder-gray-600"
                    />
                  ) : jsonSchemaFields.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {jsonSchemaFields.map((field, i) => (
                        <div key={i} className={`${INSET} p-2.5 sm:p-3`}>
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <span className="break-all font-mono text-xs font-medium text-white sm:text-sm">
                              {field.name}
                            </span>
                            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-gray-400 sm:px-2 sm:text-xs">
                              {field.type}
                            </span>
                            {/* "required" costs a whole line on a phone. The
                                asterisk is the same convention forms already use. */}
                            {field.required && (
                              <span
                                title="Required"
                                className="rounded bg-red-900/20 px-1.5 py-0.5 text-[10px] text-red-400 sm:px-2 sm:text-xs"
                              >
                                <span className="sm:hidden" aria-hidden>*</span>
                                <span className="hidden sm:inline">required</span>
                                <span className="sr-only">Required</span>
                              </span>
                            )}
                          </div>
                          {field.description && (
                            <p className="mt-1 text-[11px] text-gray-400 sm:text-xs">
                              {field.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-gray-500">
                      No JSON schema defined — responses will not be validated.
                    </p>
                  )}
                </>
              )}

              {/* Response settings — only the ones the backend actually stores. */}
              <p className="mt-8 text-xs uppercase tracking-wide text-gray-500">Response settings</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  {
                    label: "Strict validation",
                    sub: "Responses must match the schema",
                    on: Boolean(template?.outputFormat?.strictValidation ?? true),
                  },
                  {
                    label: "Pretty print",
                    sub: "Indent the JSON output",
                    on: Boolean(template?.outputFormat?.prettyPrint ?? false),
                  },
                  {
                    label: "Indent",
                    sub: "Spaces per level",
                    value: template?.outputFormat?.indent ?? 2,
                  },
                ].map((s) => (
                  <div key={s.label} className={`${INSET} flex items-center justify-between gap-3 p-3`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white">{s.label}</p>
                      <p className="break-words text-xs text-gray-500">{s.sub}</p>
                    </div>
                    {"value" in s ? (
                      <span className="ml-3 rounded bg-white/10 px-2 py-1 text-xs text-gray-200">
                        {s.value}
                      </span>
                    ) : (
                      <span
                        className={`ml-3 flex h-5 w-9 flex-shrink-0 items-center rounded-full px-0.5 ${
                          s.on ? "bg-primary" : "bg-gray-600"
                        }`}
                      >
                        <span
                          className={`h-4 w-4 rounded-full bg-white transition-transform ${
                            s.on ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Preview — a sample response, not the schema. The schema is the
                contract; the preview is what the avatar will actually send. */}
            <div className={`${PANEL} p-4 sm:p-6`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-medium text-white sm:text-lg">Output preview</h3>
                {outputFormat === "json" && jsonSchema && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowRawSchema((v) => !v)}
                      className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-gray-300 transition-colors hover:text-white"
                    >
                      {showRawSchema ? "Preview" : "Schema"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onCopyText(showRawSchema || !previewJson ? jsonSchema : previewJson)}
                      aria-label={showRawSchema ? "Copy schema" : "Copy sample response"}
                      className="rounded-lg border border-white/10 bg-white/5 p-2 text-gray-400 transition-colors hover:text-white"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                {outputFormat !== "json"
                  ? "This avatar answers in natural language."
                  : showRawSchema
                  ? "The contract responses are validated against."
                  : "An example of what a response will look like."}
              </p>

              {outputFormat === "json" && jsonSchema ? (
                <pre className="mt-4 max-h-[520px] overflow-auto rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-xs leading-relaxed text-gray-300">
                  {showRawSchema || !previewJson ? jsonSchema : previewJson}
                </pre>
              ) : (
                <EmptyTab
                  Icon={Type}
                  title={getOutputFormatModeLabel(outputFormat)}
                  sub="Responses come back as text, with no enforced structure."
                />
              )}
            </div>
          </div>
        )}

        {/* ---------------- Examples ---------------- */}
        {tab === "examples" && (
          <div className={`${PANEL} p-4 sm:p-6`}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-medium text-white sm:text-lg">Examples</h3>
                <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                  Conversation examples used to validate and steer this avatar.
                </p>
              </div>
              {isOwner && !isEditing && (
                <Button onClick={onEdit} className="bg-primary text-white hover:bg-violet-700">
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit examples
                </Button>
              )}
            </div>

            {/* Stats line */}
            <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
              <span>
                {examples.length} example{examples.length === 1 ? "" : "s"}
              </span>
              <span className="text-gray-600">•</span>
              <span>
                {examples.reduce((n, e) => n + wordCount(e), 0)} words total
              </span>
              <span className="text-gray-600">•</span>
              <span>Updated {formatDate(updatedAtOf(template || {}))}</span>
            </div>

            {isEditing && (
              <div className="flex min-h-[380px] flex-col rounded-lg border border-white/10 bg-black/20 p-2 sm:p-4">
                <SectionsManager
                  sections={examples}
                  setSections={form.setExamples}
                  title="Examples"
                  description="Add example conversations, use cases, or documentation for this avatar"
                  addButtonLabel="Add Example"
                  deleteButtonLabel="Delete Last Example"
                  emptyLabel="No examples yet."
                  emptySubLabel="Click 'Add Example' to create your first example"
                  placeholder="Add example conversations, use cases, or documentation for this avatar..."
                  overlayMaxWidth="700px"
                  autoTitle
                  titlePrefix="Example"
                  isExample
                />
              </div>
            )}

            {!isEditing && examples.length > 0 && (
              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={exampleQuery}
                  onChange={(e) => setExampleQuery(e.target.value)}
                  placeholder="Search examples..."
                  className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none"
                />
              </div>
            )}

            {isEditing ? null : examples.length === 0 ? (
              <EmptyTab
                Icon={MessageSquare}
                title="No examples yet"
                sub="Examples are conversation cases that make this avatar's answers consistent."
              />
            ) : filteredExamples.length === 0 ? (
              <EmptyTab Icon={Search} title="No matches" sub={`Nothing matches "${exampleQuery}".`} />
            ) : (
              <>
                <div className="space-y-2">
                  {filteredExamples.map((example, idx) => (
                    <ExampleRow
                      key={example.id || idx}
                      entry={example}
                      index={idx}
                      onOpen={(e, i) => onOpenEntry(e, i, "example")}
                    />
                  ))}
                </div>

                <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-white/10 bg-black/20 p-4">
                  <ClipboardCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-400" />
                  <p className="text-xs text-gray-400">
                    <span className="text-gray-300">Tip:</span> vary your examples — covering more
                    situations is what makes an avatar's answers consistent.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {zoomed && image && <ImageLightbox src={image} alt={name} onClose={() => setZoomed(false)} />}

      {pendingImage && (
        <ImageCropper
          file={pendingImage}
          onCancel={() => setPendingImage(null)}
          onError={(msg) => {
            form.onImageError(msg);
            setPendingImage(null);
          }}
          onDone={(dataUrl) => {
            form.setAvatarBase64(dataUrl);
            form.setAvatarUrl("");
            setPendingImage(null);
          }}
        />
      )}
    </div>
  );
}
